import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, X, Info, Clock, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface FeedbackToastProps {
  type: ToastType;
  title: string;
  description?: string;
  isOpen: boolean;
  onClose: () => void;
  duration?: number;
  showProgress?: boolean;
  position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  realm?: 'outer' | 'sanctuary';
  actionLabel?: string;
  onAction?: () => void;
}

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: Clock,
  info: Info,
  loading: Zap
};

const bgColors = {
  outer: {
    success: 'bg-emerald-50 border-emerald-200',
    error: 'bg-red-50 border-red-200',
    warning: 'bg-amber-50 border-amber-200',
    info: 'bg-blue-50 border-blue-200',
    loading: 'bg-indigo-50 border-indigo-200'
  },
  sanctuary: {
    success: 'bg-emerald-900/30 backdrop-blur-sm border-emerald-700/50',
    error: 'bg-red-900/30 backdrop-blur-sm border-red-700/50',
    warning: 'bg-amber-900/30 backdrop-blur-sm border-amber-700/50',
    info: 'bg-blue-900/30 backdrop-blur-sm border-blue-700/50',
    loading: 'bg-indigo-900/30 backdrop-blur-sm border-indigo-700/50'
  }
};

const iconColors = {
  outer: {
    success: 'text-emerald-500',
    error: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500',
    loading: 'text-indigo-500'
  },
  sanctuary: {
    success: 'text-emerald-300',
    error: 'text-red-300',
    warning: 'text-amber-300',
    info: 'text-blue-300',
    loading: 'text-indigo-300'
  }
};

const textColors = {
  outer: {
    title: {
      success: 'text-emerald-800',
      error: 'text-red-800',
      warning: 'text-amber-800',
      info: 'text-blue-800',
      loading: 'text-indigo-800'
    },
    description: {
      success: 'text-emerald-600',
      error: 'text-red-600',
      warning: 'text-amber-600',
      info: 'text-blue-600',
      loading: 'text-indigo-600'
    }
  },
  sanctuary: {
    title: {
      success: 'text-emerald-100',
      error: 'text-red-100',
      warning: 'text-amber-100',
      info: 'text-blue-100',
      loading: 'text-indigo-100'
    },
    description: {
      success: 'text-emerald-200/80',
      error: 'text-red-200/80',
      warning: 'text-amber-200/80',
      info: 'text-blue-200/80',
      loading: 'text-indigo-200/80'
    }
  }
};

const progressColors = {
  outer: {
    success: 'bg-emerald-400',
    error: 'bg-red-400',
    warning: 'bg-amber-400',
    info: 'bg-blue-400',
    loading: 'bg-indigo-400'
  },
  sanctuary: {
    success: 'bg-emerald-400/70',
    error: 'bg-red-400/70',
    warning: 'bg-amber-400/70',
    info: 'bg-blue-400/70',
    loading: 'bg-indigo-400/70'
  }
};

const buttonColors = {
  outer: {
    success: 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800',
    error: 'bg-red-100 hover:bg-red-200 text-red-800',
    warning: 'bg-amber-100 hover:bg-amber-200 text-amber-800',
    info: 'bg-blue-100 hover:bg-blue-200 text-blue-800',
    loading: 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800'
  },
  sanctuary: {
    success: 'bg-emerald-800/40 hover:bg-emerald-700/60 text-emerald-100',
    error: 'bg-red-800/40 hover:bg-red-700/60 text-red-100',
    warning: 'bg-amber-800/40 hover:bg-amber-700/60 text-amber-100',
    info: 'bg-blue-800/40 hover:bg-blue-700/60 text-blue-100',
    loading: 'bg-indigo-800/40 hover:bg-indigo-700/60 text-indigo-100'
  }
};

const closeButtonColors = {
  outer: {
    success: 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100',
    error: 'text-red-600 hover:text-red-800 hover:bg-red-100',
    warning: 'text-amber-600 hover:text-amber-800 hover:bg-amber-100',
    info: 'text-blue-600 hover:text-blue-800 hover:bg-blue-100',
    loading: 'text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100'
  },
  sanctuary: {
    success: 'text-emerald-300 hover:text-emerald-100 hover:bg-emerald-800/40',
    error: 'text-red-300 hover:text-red-100 hover:bg-red-800/40',
    warning: 'text-amber-300 hover:text-amber-100 hover:bg-amber-800/40',
    info: 'text-blue-300 hover:text-blue-100 hover:bg-blue-800/40',
    loading: 'text-indigo-300 hover:text-indigo-100 hover:bg-indigo-800/40'
  }
};

const positions = {
  'top': 'top-4 left-1/2 -translate-x-1/2',
  'bottom': 'bottom-4 left-1/2 -translate-x-1/2',
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4'
};

export const FeedbackToast: React.FC<FeedbackToastProps> = ({
  type,
  title,
  description,
  isOpen,
  onClose,
  duration = 5000,
  showProgress = true,
  position = 'bottom',
  realm = 'outer',
  actionLabel,
  onAction
}) => {
  const IconComponent = icons[type];
  
  // Auto-close after duration
  React.useEffect(() => {
    if (isOpen && duration > 0 && type !== 'loading') {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose, type]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: position.includes('top') ? -20 : 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: position.includes('top') ? -20 : 20 }}
          transition={{ duration: 0.3 }}
          className={cn(
            'fixed z-50 shadow-lg rounded-lg border px-4 py-3 min-w-[320px] max-w-md',
            positions[position],
            bgColors[realm][type]
          )}
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 pt-0.5">
              {type === 'loading' ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                >
                  <IconComponent className={cn('w-5 h-5', iconColors[realm][type])} />
                </motion.div>
              ) : (
                <IconComponent className={cn('w-5 h-5', iconColors[realm][type])} />
              )}
            </div>
            
            <div className="flex-1">
              <h4 className={cn('font-medium', textColors[realm].title[type])}>
                {title}
              </h4>
              {description && (
                <p className={cn('mt-1 text-sm', textColors[realm].description[type])}>
                  {description}
                </p>
              )}
              {actionLabel && onAction && (
                <button
                  onClick={onAction}
                  className={cn(
                    'mt-2 px-3 py-1 text-sm font-medium rounded-md transition-colors',
                    buttonColors[realm][type]
                  )}
                >
                  {actionLabel}
                </button>
              )}
            </div>
            
            <button
              onClick={onClose}
              className={cn(
                'shrink-0 rounded-full p-1 transition-colors',
                closeButtonColors[realm][type]
              )}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {showProgress && duration > 0 && type !== 'loading' && (
            <div className="mt-2 h-1 w-full bg-black/5 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: '100%' }}
                animate={{ width: 0 }}
                transition={{ duration: duration / 1000, ease: 'linear' }}
                className={cn('h-full rounded-full', progressColors[realm][type])}
              />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Simple hook for managing toast state
export const useFeedbackToast = (initialState = false) => {
  const [isOpen, setIsOpen] = React.useState(initialState);
  
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(prev => !prev);
  
  return { isOpen, open, close, toggle };
};
